import { requireCompletedProfile } from "@hisani/auth";
import { canUseStableFeature, canViewStableFeature, } from "../domain/membership/authorization-policy.js";
import { canAddSealedRecord } from "../domain/horses/horse-lifecycle.js";
import { addFeedRecordSchema, addTemperatureRecordSchema, addWeightRecordSchema, listFeedRecordsSchema, listTemperatureRecordsSchema, listWeightRecordsSchema, stableForbidden, stableInvalidTransition, stableNotFound, } from "../validation.js";
async function requireCareRecordLogger(dependencies, stableId, horsemanId) {
    const membership = await dependencies.repository.getMembershipContext(stableId, horsemanId);
    if (membership === null)
        throw stableNotFound();
    if (!canUseStableFeature(membership, "CARE_RECORDS"))
        throw stableForbidden();
    return membership;
}
async function requireCareRecordViewer(dependencies, stableId, horsemanId) {
    const membership = await dependencies.repository.getMembershipContext(stableId, horsemanId);
    if (membership === null)
        throw stableNotFound();
    if (!canViewStableFeature(membership, "CARE_RECORDS"))
        throw stableForbidden();
    return membership;
}
/**
 * A Care Record can only be logged against a Horse that is currently
 * accepting new Sealed entries — `docs/products/stable.md`: "A record cannot
 * be created against a Horse that fails the capability policy."
 */
async function requireHorseAcceptingCareRecords(dependencies, stableId, horseId) {
    const horse = await dependencies.repository.getHorse(stableId, horseId);
    if (horse === null)
        throw stableNotFound();
    if (!canAddSealedRecord(horse)) {
        throw stableInvalidTransition("This horse is not currently accepting new Sealed records.");
    }
    return horse;
}
export async function addWeightRecord(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = addWeightRecordSchema.parse(input);
    await requireCareRecordLogger(dependencies, parsed.stableId, actor.account.id);
    await requireHorseAcceptingCareRecords(dependencies, parsed.stableId, parsed.horseId);
    const now = dependencies.clock.now();
    return dependencies.repository.addWeightRecord({
        id: dependencies.newId(),
        stableId: parsed.stableId,
        horseId: parsed.horseId,
        actorId: actor.account.id,
        weight: parsed.weight,
        eventTime: parsed.eventTime,
        now,
    });
}
export async function listWeightRecords(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listWeightRecordsSchema.parse(input);
    await requireCareRecordViewer(dependencies, parsed.stableId, actor.account.id);
    return dependencies.repository.listWeightRecords(parsed);
}
export async function addTemperatureRecord(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = addTemperatureRecordSchema.parse(input);
    await requireCareRecordLogger(dependencies, parsed.stableId, actor.account.id);
    await requireHorseAcceptingCareRecords(dependencies, parsed.stableId, parsed.horseId);
    const now = dependencies.clock.now();
    return dependencies.repository.addTemperatureRecord({
        id: dependencies.newId(),
        stableId: parsed.stableId,
        horseId: parsed.horseId,
        actorId: actor.account.id,
        temperature: parsed.temperature,
        eventTime: parsed.eventTime,
        now,
    });
}
export async function listTemperatureRecords(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listTemperatureRecordsSchema.parse(input);
    await requireCareRecordViewer(dependencies, parsed.stableId, actor.account.id);
    return dependencies.repository.listTemperatureRecords(parsed);
}
export async function addFeedRecord(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = addFeedRecordSchema.parse(input);
    await requireCareRecordLogger(dependencies, parsed.stableId, actor.account.id);
    await requireHorseAcceptingCareRecords(dependencies, parsed.stableId, parsed.horseId);
    const now = dependencies.clock.now();
    return dependencies.repository.addFeedRecord({
        id: dependencies.newId(),
        stableId: parsed.stableId,
        horseId: parsed.horseId,
        actorId: actor.account.id,
        description: parsed.description,
        amount: parsed.amount,
        eventTime: parsed.eventTime,
        now,
    });
}
export async function listFeedRecords(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listFeedRecordsSchema.parse(input);
    await requireCareRecordViewer(dependencies, parsed.stableId, actor.account.id);
    return dependencies.repository.listFeedRecords(parsed);
}
